import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { DataTable, DataTableColumnHeader } from '@cargomatic/ui';
import {
  DocumentNameCell,
  DocumentTypeCell,
  DocumentDateCell,
  DocumentSignatoryCell,
  DocumentStatusCell,
  DocumentUserCell,
  DocumentDeleteCell,
} from './Document';

export function DocumentsList({
  isOrder,
  documents,
  deleteDisabled,
  onDeleteConfirm,
  onDeleteDecline,
  sentDocuments,
  loading,
  pagination,
  setPagination,
  pageCount,
}) {
  const { t } = useTranslation();
  const entity = isOrder ? 'order' : 'shipment';
  const sentDocumentIds = useMemo(
    () => new Set((sentDocuments || []).map((doc) => doc.id)),
    [sentDocuments],
  );

  const columns = useMemo(
    () => [
      {
        id: 'document',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DOCUMENT')} />
        ),
        cell: ({ row }) => <DocumentNameCell document={row.original} />,
        enableSorting: false,
      },
      {
        id: 'documentType',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DOCUMENT_TYPE')} />
        ),
        cell: ({ row }) => <DocumentTypeCell document={row.original} />,
        enableSorting: false,
      },
      {
        id: 'date',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DATE_one')} />
        ),
        cell: ({ row }) => <DocumentDateCell document={row.original} />,
        enableSorting: false,
      },
      {
        id: 'signatory',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('SIGNATORY')} />
        ),
        cell: ({ row }) => (
          <DocumentSignatoryCell
            document={row.original}
            isSent={sentDocumentIds.has(row.original.id)}
          />
        ),
        enableSorting: false,
      },
      {
        id: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('STATUS')} />
        ),
        cell: ({ row }) => <DocumentStatusCell document={row.original} />,
        enableSorting: false,
      },
      {
        id: 'user',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('USER')} />
        ),
        cell: ({ row }) => <DocumentUserCell document={row.original} />,
        enableSorting: false,
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <DocumentDeleteCell
            isOrder={isOrder}
            document={row.original}
            deleteDisabled={deleteDisabled}
            onConfirm={onDeleteConfirm}
            onDecline={onDeleteDecline}
          />
        ),
        enableSorting: false,
      },
    ],
    [
      isOrder,
      deleteDisabled,
      onDeleteConfirm,
      onDeleteDecline,
      sentDocumentIds,
      t,
    ],
  );

  const table = useReactTable({
    data: documents || [],
    columns,
    state: pagination ? { pagination } : undefined,
    pageCount: pagination ? pageCount : undefined,
    manualPagination: Boolean(pagination),
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto" data-testid="shipment-documents-table">
      <DataTable
        table={table}
        isLoading={loading}
        emptyResultsText={t('THIS_ENTITY_HAS_NO_DOCUMENTS', { entity })}
      />
    </div>
  );
}
